import type { MessageKey, Translate } from '@/i18n';
import type { GameConfig, HubGame } from '@/types';

export interface Spec {
  label: string;
  value: string;
}

/** A spec whose value is a set of figures, e.g. the three certified RTPs. */
export interface SpecGroup {
  label: string;
  items: string[];
}

const CURRENCY_FALLBACK = ['EUR', 'USD', 'TRY', 'GBP'];
/** Matches event-management `PLAYER_MESSAGE_LOCALES` (JWT `language`). */
export const SLOT_PLAYER_LANGUAGES = [
  'en',
  'tr',
  'es',
  'fr',
  'ru',
  'ja',
  'zh',
  'ar',
  'de',
  'pt',
  'bg',
  'la',
] as const;

const LANGUAGE_LABEL_FALLBACK: Record<string, string> = {
  en: 'English',
  tr: 'Türkçe',
  es: 'Español',
  fr: 'Français',
  ru: 'Русский',
  ja: '日本語',
  zh: '中文',
  ar: 'العربية',
  de: 'Deutsch',
  pt: 'Português',
  bg: 'Български',
  la: 'Latina',
};

/** Interface languages currently published on live tables. */
export const LIVE_UI_LANGUAGES = ['en', 'tr', 'ru', 'pt'] as const;

/** Language names come from the platform, translated into the reader's locale. */
export function languageName(code: string, locale: string): string {
  try {
    const display = new Intl.DisplayNames([locale], { type: 'language' });
    const name = display.of(code);
    if (name && name !== code) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    /* unsupported locale or code */
  }
  return LANGUAGE_LABEL_FALLBACK[code] || code.toUpperCase();
}

/** Config keys rendered as headline specs, so the rules table can skip them. */
const PROMOTED_CONFIG_KEYS = new Set([
  'rtp',
  'volatility',
  'maxWin',
  'paylines',
  'gridSize',
  'defaultBet',
  'betLevels',
  'features',
  'jackpotType',
  'hasJackpot',
  'bonusFrequency',
  'theme',
]);

export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/^is|^has|^num(ber)?Of|^timeout/i, (match) => `${match} `)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function formatValue(key: string, value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'number') {
    if (/^timeout/i.test(key) && value >= 1000) return `${Math.round(value / 100) / 10}s`;
    return new Intl.NumberFormat('en-US').format(value);
  }

  if (Array.isArray(value)) {
    return value.length ? value.map((item) => formatValue(key, item)).join(', ') : '—';
  }

  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

function titleCase(value?: string | null): string {
  if (!value) return '—';
  return value
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Upstream systems spell the same config field in several casings. */
const slugKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');

function configIndex(config: GameConfig): Map<string, unknown> {
  const index = new Map<string, unknown>();
  for (const [key, value] of Object.entries(config)) index.set(slugKey(key), value);
  return index;
}

function pickValue(index: Map<string, unknown>, ...aliases: string[]): unknown {
  for (const alias of aliases) {
    const value = index.get(alias);
    if (value != null && value !== '') return value;
  }
  return undefined;
}

function formatNumber(value: unknown, locale: string): string | undefined {
  if (value == null || value === '') return undefined;
  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/[,\s]/g, ''));
  if (!Number.isFinite(numeric)) return String(value).trim() || undefined;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(numeric);
}

/** "6x5" carries reels and rows when the platform does not send them apart. */
function gridPart(gridSize: string | undefined, part: 0 | 1): string | undefined {
  const parts = String(gridSize ?? '').split(/[x×*]/i);
  if (parts.length !== 2) return undefined;
  return parts[part].trim() || undefined;
}

function toList(value: unknown): string[] {
  if (value == null || value === '') return [];
  const raw = Array.isArray(value) ? value : String(value).split(/[,;|/]/);
  return raw.map((item) => String(item).trim()).filter(Boolean);
}

function suffixed(items: string[], suffix: string): string[] {
  return items.map((item) => (item.toLowerCase().endsWith(suffix.toLowerCase()) ? item : `${item}${suffix}`));
}

/** Sheet rows for a slot, in the order of the operator game sheet. */
function slotSpecs(game: HubGame, t: Translate, locale: string): Spec[] {
  const details = game.details ?? {};
  const config = details.config ?? {};
  const index = configIndex(config);
  const specs: Spec[] = [];

  const push = (label: string, value: string | null | undefined) => {
    if (value && value !== '—') specs.push({ label, value });
  };
  const pushNumber = (label: string, value: unknown) => push(label, formatNumber(value, locale));

  push(t('spec.releaseDate'), formatDate(details.releaseDate, locale));
  push(t('spec.symbol'), details.symbol);
  push(t('spec.numericId'), details.referenceId ? String(details.referenceId) : undefined);
  pushNumber(t('spec.minBetPerLine'), pickValue(index, 'minbetperline', 'minimumbetperline', 'minlinebet'));
  pushNumber(t('spec.minTotalBet'), pickValue(index, 'mintotalbet', 'minimumtotalbet', 'minbet'));
  pushNumber(t('spec.defaultBet'), config.defaultBet);
  pushNumber(t('spec.maxTotalBet'), pickValue(index, 'maxtotalbet', 'maximumtotalbet', 'maxbet'));
  pushNumber(
    t('spec.maxTotalBetAnte'),
    pickValue(index, 'maxtotalbetwithante', 'maximumtotalbetwithante', 'maxbetwithante', 'maxantetotalbet'),
  );
  push(
    t('spec.reels'),
    formatNumber(pickValue(index, 'reels', 'reelcount'), locale) ?? gridPart(config.gridSize, 0),
  );
  push(
    t('spec.rows'),
    formatNumber(pickValue(index, 'rows', 'rowcount'), locale) ?? gridPart(config.gridSize, 1),
  );

  const payType = pickValue(index, 'paytype', 'paymechanic');
  push(
    t('spec.payType'),
    payType
      ? titleCase(String(payType))
      : config.paylines != null
        ? t('spec.payLinesValue', { count: formatValue('paylines', config.paylines) })
        : undefined,
  );

  push(t('spec.symbolPays'), (() => {
    const pays = toList(pickValue(index, 'symbolpays', 'symbolpay'));
    return pays.length > 1 ? `${pays[0]} – ${pays[pays.length - 1]}` : pays[0];
  })());
  pushNumber(t('spec.betMultiplier'), pickValue(index, 'betmultiplier'));
  pushNumber(
    t('spec.maxMultiplierWin'),
    pickValue(index, 'maxmultiplierwin', 'maxwinmultiplier') ?? config.maxWin,
  );
  pushNumber(t('spec.maxExposure'), pickValue(index, 'maxexposure'));
  push(t('spec.volatility'), config.volatility ? titleCase(config.volatility) : undefined);

  return specs;
}

/** Multi-value sheet rows: bonus buy, ante bet, certified RTPs, hit frequency. */
export function buildSpecGroups(game: HubGame, t: Translate, locale: string): SpecGroup[] {
  if (game.category === 'live') return [];

  const config = game.details?.config ?? {};
  const index = configIndex(config);
  const groups: SpecGroup[] = [];

  const push = (label: string, items: string[]) => {
    if (items.length > 0) groups.push({ label, items });
  };

  push(
    t('spec.bonusBuyX'),
    suffixed(toList(pickValue(index, 'bonusbuyx', 'bonusbuy', 'buybonusx', 'buyfeaturex')), 'x'),
  );
  push(t('spec.anteBetX'), suffixed(toList(pickValue(index, 'antebetx', 'antebetmultiplier')), 'x'));

  const rtps = toList(pickValue(index, 'rtps', 'rtplist', 'certifiedrtps')) ;
  push(t('spec.rtps'), suffixed(rtps.length > 0 ? rtps : toList(config.rtp), '%'));

  push(t('spec.hitFrequency'), hitFrequencyItems(config, t, locale));

  return groups;
}

function hitFrequencyItems(config: GameConfig, t: Translate, locale: string): string[] {
  const items: string[] = [];

  const add = (key: string, value: unknown) => {
    const formatted = formatNumber(value, locale);
    if (!formatted) return;
    const odds = t('spec.oneIn', { value: formatted });
    items.push(slugKey(key) === 'hitfrequency' ? odds : `${humanizeKey(key)} ${odds}`);
  };

  for (const [key, value] of Object.entries(config)) {
    const slug = slugKey(key);
    if (!slug.includes('hitfrequency') && !slug.startsWith('oddsfor')) continue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [subKey, subValue] of Object.entries(value as Record<string, unknown>)) {
        add(subKey, subValue);
      }
      continue;
    }
    add(key, value);
  }

  return items;
}

const FLAG_SPECS: Array<{ key: MessageKey; aliases: string[] }> = [
  { key: 'spec.hasReplay', aliases: ['hasreplay'] },
  { key: 'spec.hasBuySpins', aliases: ['hasbuyspins', 'hasbuyfeature'] },
  { key: 'spec.hasInstantBonus', aliases: ['hasinstantbonus'] },
  { key: 'spec.hasHyperplay', aliases: ['hashyperplay'] },
  { key: 'spec.hasAnteBet', aliases: ['hasantebet'] },
];

/** Feature switches, shown only when the platform states them either way. */
export function buildSpecFlags(game: HubGame, t: Translate): Spec[] {
  if (game.category === 'live') return [];

  const index = configIndex(game.details?.config ?? {});

  return FLAG_SPECS.flatMap(({ key, aliases }) => {
    const value = pickValue(index, ...aliases);
    const enabled =
      typeof value === 'boolean'
        ? value
        : /^(yes|true|1)$/i.test(String(value ?? ''))
          ? true
          : /^(no|false|0)$/i.test(String(value ?? ''))
            ? false
            : null;

    if (enabled === null) return [];
    return [{ label: t(key), value: enabled ? t('common.yes') : t('common.no') }];
  });
}

/** Headline specification table, mirroring an operator game sheet. */
export function buildSpecs(game: HubGame, t: Translate, locale: string): Spec[] {
  if (game.category !== 'live') return slotSpecs(game, t, locale);

  const details = game.details ?? {};
  const config = details.config ?? {};
  const specs: Spec[] = [];

  const yesNo = (value: unknown) => (value ? t('common.yes') : t('common.no'));

  const push = (label: string, value: string | null | undefined) => {
    if (value && value !== '—') specs.push({ label, value });
  };

  push(t('spec.releaseDate'), formatDate(details.releaseDate, locale));
  push(t('spec.symbol'), details.symbol);
  push(t('spec.referenceId'), details.referenceId);
  push(t('spec.provider'), details.provider);
  push(t('spec.product'), game.category === 'live' ? t('game.liveCasino') : t('game.slots'));
  push(t('spec.gameType'), titleCase(details.gameType));
  push(t('spec.category'), titleCase(details.categoryLabel));
  push(t('spec.theme'), titleCase(details.theme));
  push(t('spec.technology'), details.technology);

  if (config.rtp != null) push(t('spec.rtp'), `${config.rtp}%`);
  if (config.volatility) push(t('spec.volatility'), titleCase(config.volatility));
  if (config.maxWin != null) push(t('spec.maxWin'), `${formatValue('maxWin', config.maxWin)}x`);
  if (config.paylines != null) push(t('spec.paylines'), formatValue('paylines', config.paylines));
  if (config.gridSize) push(t('spec.grid'), config.gridSize);
  if (config.bonusFrequency != null) {
    push(
      t('spec.bonusFrequency'),
      t('spec.bonusFrequencyValue', { value: formatValue('bonusFrequency', config.bonusFrequency) }),
    );
  }
  if (config.hasJackpot != null) {
    push(t('spec.jackpot'), config.hasJackpot ? titleCase(config.jackpotType) : t('common.none'));
  }

  if (config.betLevels?.length) {
    const levels = config.betLevels;
    push(t('spec.betRange'), `${Math.min(...levels)} – ${Math.max(...levels)}`);
  }
  if (config.defaultBet != null) {
    push(t('spec.defaultBet'), formatValue('defaultBet', config.defaultBet));
  }

  push(t('spec.currency'), details.currency);

  push(t('spec.demoAvailable'), yesNo(details.hasDemo));
  push(t('spec.tournaments'), yesNo(details.hasTournament));
  push(t('spec.highlighted'), yesNo(details.highlighted));
  push(t('spec.status'), game.flags?.isActive === false ? t('common.inactive') : t('common.active'));
  if (details.version != null) push(t('spec.version'), String(details.version));
  push(t('spec.lastUpdated'), formatDate(details.updatedAt, locale));

  return specs;
}

/** Remaining upstream config entries — live table rules, timeouts, side bets. */
export function buildRules(game: HubGame): Spec[] {
  const config = game.details?.config ?? {};

  return Object.entries(config)
    .filter(([key, value]) => !PROMOTED_CONFIG_KEYS.has(key) && typeof value !== 'object')
    .map(([key, value]) => ({ label: humanizeKey(key), value: formatValue(key, value) }));
}

export function currencyOptions(game: HubGame): string[] {
  const preferred = game.details?.currency ? [game.details.currency] : [];
  return Array.from(new Set([...preferred, ...CURRENCY_FALLBACK]));
}

/** Stamp the demo launch URL with the toolbar language so the game does not depend on JWT alone. */
export function withLaunchLanguage(url: string, language: string): string {
  if (!url || !language) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('language', language);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function uiLanguageCodes(game: HubGame): readonly string[] {
  const isLive = game.category === 'live' || game.details?.isLive;
  return isLive ? LIVE_UI_LANGUAGES : SLOT_PLAYER_LANGUAGES;
}

export function languageOptions(
  game: HubGame,
  locale: string,
): Array<{ code: string; label: string }> {
  return uiLanguageCodes(game).map((code) => ({ code, label: languageName(code, locale) }));
}
